import { useMutation } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';
import { Nullable } from '@tcc/common';
import { AppDispatch } from 'store';
import {
  SeasonName,
  UapiAgronomicActivityType,
  UapiMachineDataMapName,
  UapiSeasonName,
} from '../../../graphql/sdk';
import {
  toNumericLegendValue,
  updateMachineDataMapLegendARD,
} from '../../../requesters/agVizRequester/editLegendARD';
import { reqAgVizMaps } from '../../../actions/agVizActions';
import {
  dmbLayerIdToUapiLayerName,
  getActivityTypeFromDmbLayerId,
} from '../../../utils/agVizLayerMapper';
import { parseSeasonTag } from '../../../utils/seasonUtils';
import { logDmbErrorLocation } from '../../../utils/error';
import { useAppSelector } from '../../../utils';

const CONTEXT = 'useEditLegendARD';

export interface EditLegendARDVariables {
  layerId: string;
  seasonId: string;
  minValue: Nullable<string>;
  maxValue: Nullable<string>;
  steps: Nullable<number>;
}

/**
 * Ag-Viz (ARD) legend edition
 *
 * Only used when Ag-Viz is ON and the `dmb-ag-viz-legend-edition` feature flag
 * is ON; the caller guards on those flags before calling `mutate`.
 */
export const useEditLegendARD = () => {
  const dispatch = useDispatch<AppDispatch>();
  const fieldId = useAppSelector((state) => state.field?.uuid);
  const crops = useAppSelector((state) => state.crops);
  const seasonsList = useAppSelector((state) => state.seasons?.list);

  return useMutation({
    mutationFn: async ({ layerId, seasonId, minValue, maxValue, steps }: EditLegendARDVariables) => {
      const [, cropCode] = seasonId.split(':');
      const cropId = crops?.[cropCode]?.['dfp-uuid'];
      const activityType = getActivityTypeFromDmbLayerId(layerId) as
        | UapiAgronomicActivityType
        | undefined;
      const layer = dmbLayerIdToUapiLayerName(layerId) as UapiMachineDataMapName | undefined;

      if (!fieldId || !cropId || !activityType || !layer) {
        throw new Error(
          `Missing identifiers for ARD legend edit (fieldId: ${fieldId}, cropId: ${cropId}, activityType: ${activityType}, layer: ${layer})`
        );
      }

      if (!steps || steps <= 0) {
        throw new Error(
          `Invalid number of buckets for ARD legend edit (steps: ${steps}); must be greater than 0`
        );
      }

      const { seasonName, year } = parseSeasonTag(seasonId, seasonsList?.[seasonId]);

      const payload = await updateMachineDataMapLegendARD({
        fieldId,
        cropId,
        seasonName: (seasonName as unknown) as UapiSeasonName,
        seasonYear: year,
        activityType,
        layer,
        start: toNumericLegendValue(minValue),
        end: toNumericLegendValue(maxValue),
        numberOfBuckets: steps,
      });

      if (payload?.errors?.length) {
        const reason = payload.errors.map((error) => error.reason).join('; ');
        throw new Error(`updateMachineDataMapLegend failed: ${reason}`);
      }

      return { cropCode, seasonName, year, activityType };
    },
    onSuccess: ({ cropCode, seasonName, year, activityType }) => {
      // Refresh the Ag-Viz maps so the updated legend + tiles are pulled from the server.
      dispatch(
        reqAgVizMaps({
          fieldId: fieldId as string,
          cropCode,
          seasonName: (seasonName as unknown) as SeasonName,
          seasonYear: year,
          activityType,
        })
      );
    },
    onError: (error) => {
      logDmbErrorLocation(error, CONTEXT);
    },
  });
};
